import React, { useCallback, useState } from 'react';
import { usePopover, Popover } from '@trello/nachos/popover';
import { Button } from '@trello/nachos/button';
import styles from './DeleteBoardButton.less';
import { forNamespace } from '@trello/i18n';
import { useDeleteBoardMutation } from './DeleteBoardMutation.generated';
import { Analytics, SourceType } from '@trello/atlassian-analytics';
import { CloseBoardTestIds } from '@trello/test-ids';
import { navigate } from 'app/scripts/controller/navigate';

const formatConfirm = forNamespace('confirm');
const formatDelete = forNamespace('closed board view');

interface DeleteBoardButtonProps {
  boardId: string;
  source: SourceType;
}

export const DeleteBoardButton: React.FC<DeleteBoardButtonProps> = ({
  boardId,
  source,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    triggerRef,
    toggle,
    popoverProps,
    hide,
  } = usePopover<HTMLButtonElement>();
  const [deleteBoard] = useDeleteBoardMutation();

  const onDelete = useCallback(async () => {
    hide();

    setIsDeleting(true);

    const traceId = Analytics.startTask({
      taskName: 'delete-board',
      source,
    });

    try {
      await deleteBoard({
        variables: { boardId },
        // The websocket message that the board has been deleted can take
        // several seconds to arrive, but we need it immediately as this
        // operation redirects to a page that needs it.
        update: (cache) => {
          cache.evict({
            id: cache.identify({ id: boardId, __typename: 'Board' }),
          });
          cache.gc();
        },
      });

      Analytics.sendTrackEvent({
        action: 'deleted',
        actionSubject: 'board',
        source,
        containers: {
          board: {
            id: boardId,
          },
        },
        attributes: {
          taskId: traceId,
        },
      });
      Analytics.taskSucceeded({
        taskName: 'delete-board',
        source,
        traceId,
      });

      navigate('/', { trigger: true, replace: true });
    } catch (err) {
      // TODO add error state
      Analytics.taskFailed({
        taskName: 'delete-board',
        source,
        error: err,
        traceId,
      });
    }
  }, [hide, source, deleteBoard, boardId]);

  return (
    <>
      <Button
        className={styles.linkVersion}
        onClick={toggle}
        isDisabled={isDeleting}
        ref={triggerRef}
        testId={CloseBoardTestIds.DeleteBoardButton}
      >
        {formatDelete('permanently delete board')}
      </Button>
      <Popover
        {...popoverProps}
        title={formatConfirm(['delete board', 'title'])}
      >
        {formatConfirm(['delete board', 'text'])}
        <Button
          appearance={'danger'}
          size="fullwidth"
          className={styles.confirmButton}
          onClick={onDelete}
          testId={CloseBoardTestIds.DeleteBoardConfirmButton}
        >
          {formatConfirm(['delete board', 'confirm'])}
        </Button>
      </Popover>
    </>
  );
};
